import React, { useState, useEffect } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  formatDate,
  formatDateTime,
  formatTime,
  useTimeFormat,
} from "helpers/timeFormat";

import { playersListLimits } from "configs/limits";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Pagination from "components/Pagination";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import styles from "./ListPromocodesTab.module.scss";

interface UserPromocodeHistoryItem {
  id: number;
  promoCode: string;
  activationArea: string;
  targetDomain: string;
  targetBonusTitle: string;
  status: string;
  depositId: number | null;
  appliedAt: string;
}

const promocodeStatusClassMap: Record<string, string> = {
  pending: "badge--warning",
  applied: "badge--success",
  rejected: "badge--danger",
};

const getPromocodeStatusClass = (status: string) => {
  const statusKey = status.trim().toLowerCase();
  return promocodeStatusClassMap[statusKey] ?? "badge--neutral";
};

const ListPromocodesTab: React.FC = () => {
  const timeFormat = useTimeFormat();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [list, setList] = useState<UserPromocodeHistoryItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(playersListLimits[0]);
  const [isFetched, setIsFetched] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoadList = async () => {
    setIsFetched(false);
    setHasError(false);
    const entityId = searchParams.get("entityId");
    if (!entityId) {
      setList([]);
      setTotalItems(0);
      setTotalPages(0);
      setIsFetched(true);
      return;
    }
    const offset = (activePage - 1) * currentLimit;
    try {
      const result = await instance.get(
        `${apiRoutes.playerPromocodes}/${entityId}?limit=${currentLimit}&offset=${offset}`,
      );
      setList(result.data.items || []);
      setTotalItems(result.data.totalItems || 0);
      setTotalPages(result.data.totalPages || 0);
    } catch {
      setList([]);
      setTotalItems(0);
      setTotalPages(0);
      setHasError(true);
    }
    setIsFetched(true);
  };

  useEffect(() => {
    handleLoadList();
  }, [activePage, currentLimit]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      setActivePage(activePage - 1);
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      setActivePage(activePage + 1);
    }
  };

  return (
    <div className={`${styles.promocodes} player-content-wrapper`}>
      {isFetched ? (
        <>
          <div className={`${styles.table} inner-table-wrapper`}>
            <table>
              <thead>
                <tr>
                  <th>{t("promoCode")}</th>
                  <th>{t("activationArea")}</th>
                  <th>{t("target")}</th>
                  <th>{t("targetBonus")}</th>
                  <th>{t("status")}</th>
                  <th>{t("deposit")}</th>
                  <th>{t("appliedAt")}</th>
                </tr>
              </thead>
              <tbody>
                {hasError ? (
                  <tr>
                    <td colSpan={7}>{t("error")}</td>
                  </tr>
                ) : (
                  list.map((el) => (
                    <tr key={el.id}>
                      <td>{el.promoCode}</td>
                      <td>{el.activationArea}</td>
                      <td>{el.targetDomain}</td>
                      <td>{el.targetBonusTitle}</td>
                      <td className={styles.statusCell}>
                        {el.status ? (
                          <span
                            className={cn(
                              "badge",
                              getPromocodeStatusClass(el.status),
                            )}
                          >
                            {el.status}
                          </span>
                        ) : null}
                      </td>
                      <td>{el.depositId || ""}</td>
                      <td
                        className={`date ${styles.appliedAt}`}
                        title={
                          el.appliedAt
                            ? formatDateTime(el.appliedAt, timeFormat)
                            : undefined
                        }
                      >
                        {el.appliedAt ? (
                          <>
                            <span>{formatDate(el.appliedAt)}</span>
                            <span>{formatTime(el.appliedAt, timeFormat)}</span>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={playersListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              setActivePage(1);
            }}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        </>
      ) : (
        <div className={styles.table}>
          <ModalTabSkeleton />
        </div>
      )}
    </div>
  );
};

export default ListPromocodesTab;
